import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Ho4ArticleSidebar from '../components/Ho4ArticleSidebar';
import InsuranceArticleSidebar from '../components/InsuranceArticleSidebar';
import { HO4_ARTICLE_CATEGORIES } from '../data/ho4Articles';
import { PET_ARTICLE_CATEGORIES } from '../data/petArticles';
import { AUTO_ARTICLE_CATEGORIES } from '../data/autoArticles';
import type { InsuranceArticleCategory } from '../data/insuranceArticleTypes';

type ProductLine = 'renters' | 'pet' | 'auto';

type IndexedArticle = {
  title: string;
  href: string;
  description: string;
  keywords: string[];
  category: string;
  product: ProductLine;
  productLabel: string;
};

function flattenCategories(
  categories: InsuranceArticleCategory[],
  product: ProductLine,
  productLabel: string
): IndexedArticle[] {
  return categories.flatMap((category) =>
    category.articles
      .filter(
        (article): article is typeof article & { href: string } =>
          article.status === 'published' && !!article.href
      )
      .map((article) => ({
        title: article.title,
        href: article.href,
        description: article.description,
        keywords: article.keywords,
        category: category.name,
        product,
        productLabel,
      }))
  );
}

const PRODUCT_TABS: { id: ProductLine; label: string }[] = [
  { id: 'renters', label: 'Renters (HO-4)' },
  { id: 'pet', label: 'Pet Insurance' },
  { id: 'auto', label: 'Auto Insurance' },
];

function KnowledgeBasePage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeProduct, setActiveProduct] = useState<ProductLine>('renters');
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 768px)').matches);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const updateMobileFlag = () => setIsMobile(mediaQuery.matches);

    updateMobileFlag();
    mediaQuery.addEventListener('change', updateMobileFlag);

    return () => {
      mediaQuery.removeEventListener('change', updateMobileFlag);
    };
  }, []);

  const allArticles = useMemo(
    () => [
      ...flattenCategories(HO4_ARTICLE_CATEGORIES, 'renters', 'Renters'),
      ...flattenCategories(PET_ARTICLE_CATEGORIES, 'pet', 'Pet'),
      ...flattenCategories(AUTO_ARTICLE_CATEGORIES, 'auto', 'Auto'),
    ],
    []
  );

  const productArticles = useMemo(
    () => allArticles.filter((article) => article.product === activeProduct),
    [allArticles, activeProduct]
  );

  const searchResults = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    if (normalizedQuery.length < 2) return [];

    const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean);

    const scored = allArticles
      .map((article) => {
        const title = article.title.toLowerCase();
        const description = article.description.toLowerCase();
        const category = article.category.toLowerCase();
        const productLabel = article.productLabel.toLowerCase();
        const keywordText = article.keywords.join(' ').toLowerCase();

        let score = 0;

        if (title.includes(normalizedQuery)) score += 8;
        if (description.includes(normalizedQuery)) score += 5;
        if (keywordText.includes(normalizedQuery)) score += 5;

        for (const token of queryTokens) {
          if (title.includes(token)) score += 3;
          if (description.includes(token)) score += 2;
          if (keywordText.includes(token)) score += 2;
          if (category.includes(token)) score += 1;
          if (productLabel.includes(token)) score += 2;
        }

        return { article, score };
      })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score || a.article.title.localeCompare(b.article.title))
      .slice(0, 10);

    return scored.map((entry) => entry.article);
  }, [allArticles, searchQuery]);

  const productSidebar = (() => {
    if (activeProduct === 'pet') {
      return (
        <InsuranceArticleSidebar
          sidebarTitle="Pet Insurance"
          categories={PET_ARTICLE_CATEGORIES}
          onNavigate={(href) => navigate(href)}
        />
      );
    }

    if (activeProduct === 'auto') {
      return (
        <InsuranceArticleSidebar
          sidebarTitle="Auto Insurance"
          categories={AUTO_ARTICLE_CATEGORIES}
          onNavigate={(href) => navigate(href)}
        />
      );
    }

    return <Ho4ArticleSidebar onNavigate={(href) => navigate(href)} />;
  })();

  const productBlurb =
    activeProduct === 'pet'
      ? 'Practical guidance for pet insurance decisions in Texas.'
      : activeProduct === 'auto'
        ? 'Practical guidance for auto insurance decisions in Texas.'
        : 'Practical guidance for HO-4 renters insurance decisions.';

  const productTabBar = (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.5rem',
        marginBottom: '1.25rem',
      }}
    >
      {PRODUCT_TABS.map((tab) => {
        const isActive = activeProduct === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveProduct(tab.id)}
            style={{
              fontFamily: 'inherit',
              fontSize: '0.9rem',
              fontWeight: isActive ? 700 : 600,
              padding: '0.45rem 0.9rem',
              borderRadius: '999px',
              border: isActive ? '1px solid var(--color-green)' : '1px solid var(--color-grey)',
              background: isActive ? '#ECFDF5' : '#FFFFFF',
              color: isActive ? '#065F46' : '#374151',
              cursor: 'pointer',
            }}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );

  const searchCard = (
    <div
      className="card mb-4"
      style={{
        border: '1px solid var(--color-green-light)',
        boxShadow: 'var(--shadow-md)',
      }}
    >
      <div className="card-body">
        <h3 style={{ marginBottom: '0.75rem', fontSize: '1.25rem' }}>Search the help center</h3>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <input
            id="kb-search"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search renters, home, pet, and auto insurance articles"
            style={{
              width: '100%',
              borderColor: 'var(--color-green)',
              boxShadow: '0 0 0 3px var(--color-green-light)',
              fontSize: '1rem',
            }}
          />

          {searchQuery.trim().length >= 2 && (
            <div
              style={{
                marginTop: '0.75rem',
                border: '1px solid var(--color-grey)',
                borderRadius: '0.5rem',
                overflow: 'hidden',
              }}
            >
              {searchResults.length > 0 ? (
                searchResults.map((article, index) => (
                  <button
                    key={article.href}
                    type="button"
                    onClick={() => navigate(article.href)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      background: '#FFFFFF',
                      border: 'none',
                      borderBottom: index === searchResults.length - 1 ? 'none' : '1px solid var(--color-grey)',
                      padding: '0.8rem 0.9rem',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontWeight: 700, color: '#111827', marginBottom: '0.2rem' }}>{article.title}</div>
                    <div style={{ fontSize: '0.8rem', color: '#6B7280', marginBottom: '0.15rem' }}>
                      {article.productLabel} · {article.category}
                    </div>
                    <div style={{ fontSize: '0.88rem', color: '#4B5563' }}>{article.description}</div>
                  </button>
                ))
              ) : (
                <div style={{ padding: '0.8rem 0.9rem', fontSize: '0.9rem', color: '#6B7280' }}>
                  No matching articles yet. Try terms like &quot;deductible&quot;, &quot;liability&quot;, &quot;pet&quot;,
                  or &quot;comprehensive&quot;.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const speakToAgentCard = (
    <div className="card mb-4">
      <div className="card-body">
        <div
          style={{
            display: 'flex',
            alignItems: isMobile ? 'stretch' : 'center',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            gap: '1rem',
          }}
        >
          <div>
            <h3 style={{ marginBottom: '0.35rem', fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
              {isMobile ? 'Need agent help?' : 'Need to speak to an insurance agent?'}
            </h3>
            <p style={{ fontSize: '0.9rem', marginBottom: 0 }}>
              If you would rather talk to a human or have questions that are not answered here, visit our Support
              Center for ways to get in touch.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => navigate('/help')}
            style={{
              letterSpacing: '0.03em',
              paddingInline: isMobile ? '1rem' : '1.5rem',
              alignSelf: isMobile ? 'flex-start' : undefined,
            }}
          >
            {isMobile ? 'Talk to agent' : 'Go to Support Center'}
          </button>
        </div>
      </div>
    </div>
  );

  const latestArticlesCard = (
    <div className="card">
      <div className="card-header" style={{ fontSize: '1.25rem' }}>
        {activeProduct === 'renters' && 'Renters insurance guides'}
        {activeProduct === 'pet' && 'Pet insurance guides'}
        {activeProduct === 'auto' && 'Auto insurance guides'}
      </div>
      <div className="card-body">
        <div className="grid grid-3">
          {productArticles.slice(0, 6).map((article) => (
            <article
              key={article.href}
              className="card"
              style={{
                boxShadow: 'none',
                borderColor: 'var(--color-grey)',
                margin: 0,
                padding: '1rem 1.25rem',
              }}
            >
              <h4 style={{ marginBottom: '0.35rem', fontSize: '1.1rem' }}>{article.title}</h4>
              <p style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                Published in {article.category}. {productBlurb}
              </p>
              <div style={{ fontSize: '0.8rem', color: '#6B7280', marginBottom: '0.75rem' }}>Published</div>
              <button type="button" className="btn btn-sm btn-primary" onClick={() => navigate(article.href)}>
                Read article
              </button>
            </article>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="container">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          gap: '1.5rem',
        }}
      >
        <div>
          <h2>Self help library</h2>
          <p style={{ marginTop: '0.5rem' }}>
            Explore {allArticles.length} guides on renters, home, pet, and auto insurance — focused on Texas coverage decisions.
          </p>
        </div>
      </div>

      {productTabBar}

      {isMobile ? (
        <section>
          {searchCard}
          {speakToAgentCard}
          <div className="mb-4">{productSidebar}</div>
          {latestArticlesCard}
        </section>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '260px 1fr',
            gap: '2rem',
          }}
        >
          {productSidebar}
          <section>
            {searchCard}
            {speakToAgentCard}
            {latestArticlesCard}
          </section>
        </div>
      )}
    </div>
  );
}

export default KnowledgeBasePage;
